import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { appComponentsStorage } from '@frameio/shared/storage';
import type { SectionId } from '../../shared/types';

const STORAGE_KEY = 'frame-io--quick-search-preferred-tab';

export const usePreferredTab = () => {
  const storageDataRef = useRef<SectionId | null>(
    appComponentsStorage.get(STORAGE_KEY) ?? null
  );
  const notifyRef = useRef<(() => void) | null>(null);

  const getSnapshot = useCallback(() => storageDataRef.current, []);

  const subscribe = useCallback((onStoreChange: () => void) => {
    notifyRef.current = onStoreChange;

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        storageDataRef.current = appComponentsStorage.get(STORAGE_KEY) ?? null;
        onStoreChange();
      }
    };

    storageDataRef.current = appComponentsStorage.get(STORAGE_KEY) ?? null;
    window.addEventListener('storage', handleStorageChange);

    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const preferredTab = useSyncExternalStore(subscribe, getSnapshot, () => null);

  const setPreferredTab = useCallback((tab: SectionId) => {
    storageDataRef.current = tab;
    appComponentsStorage.set(STORAGE_KEY, tab);

    if (notifyRef.current) {
      notifyRef.current();
    }
  }, []);

  return useMemo(
    () => ({ preferredTab, setPreferredTab }),
    [preferredTab, setPreferredTab]
  );
};
